import { useState } from "react";
import { Check, X, AlertTriangle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type EntityType = "vendor" | "customer" | "company" | "bank_account";
type FeedbackType = "correct" | "incorrect" | "partial";
type ReasonCode =
  | "wrong_entity_type"
  | "name_incomplete"
  | "wrong_entity"
  | "duplicate"
  | "internal_transfer"
  | "noise";

interface EntityFeedbackButtonProps {
  sessionId: number;
  entityType: EntityType;
  entityName: string;
  entityUid?: string;
  className?: string;
  onFeedbackSubmitted?: () => void;
}

const REASON_CODE_LABELS: Record<ReasonCode, string> = {
  wrong_entity_type: "Wrong type (vendor/customer swap)",
  name_incomplete: "Name is incomplete",
  wrong_entity: "Completely wrong entity",
  duplicate: "Duplicate entry",
  internal_transfer: "Internal transfer (not a vendor/customer)",
  noise: "Noise (bank metadata, not an entity)",
};

export function EntityFeedbackButton({
  sessionId,
  entityType,
  entityName,
  entityUid,
  className,
  onFeedbackSubmitted,
}: EntityFeedbackButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [reasonCode, setReasonCode] = useState<ReasonCode | null>(null);
  const [correctedName, setCorrectedName] = useState("");
  const [correctedUid, setCorrectedUid] = useState("");
  const [reasonText, setReasonText] = useState("");

  const submitFeedback = trpc.companyDiscovery.submitEntityFeedback.useMutation(
    {
      onSuccess: () => {
        toast.success("Feedback submitted");
        setIsOpen(false);
        resetForm();
        onFeedbackSubmitted?.();
      },
      onError: error => {
        toast.error(`Failed to submit feedback: ${error.message}`);
      },
    }
  );

  const resetForm = () => {
    setFeedbackType(null);
    setReasonCode(null);
    setCorrectedName("");
    setCorrectedUid("");
    setReasonText("");
  };

  const handleQuickFeedback = (type: FeedbackType) => {
    if (type === "correct") {
      // Submit immediately for "correct" feedback
      submitFeedback.mutate({
        sessionId,
        entityType,
        entityName,
        feedbackType: "correct",
      });
    } else {
      // Show form for incorrect/partial feedback
      setFeedbackType(type);
    }
  };

  const handleSubmit = () => {
    if (!feedbackType) return;

    submitFeedback.mutate({
      sessionId,
      entityType,
      entityName,
      feedbackType,
      correctedValue:
        correctedName || correctedUid
          ? {
              name: correctedName || undefined,
              uid: correctedUid || undefined,
              role:
                reasonCode === "wrong_entity_type"
                  ? entityType === "vendor"
                    ? "customer"
                    : "vendor"
                  : undefined,
            }
          : undefined,
      reasonCode: reasonCode || undefined,
      reasonText: reasonText || undefined,
    });
  };

  const isLoading = submitFeedback.isPending;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 px-1.5 gap-1 text-muted-foreground hover:text-foreground",
            className
          )}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <Check className="h-3.5 w-3.5 text-[var(--success)]" />
              <span className="text-muted-foreground">/</span>
              <X className="h-3.5 w-3.5 text-[var(--destructive)]" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          {!feedbackType ? (
            <>
              <div className="text-sm font-medium">
                Is this {entityType} correct?
              </div>
              <p className="text-sm text-muted-foreground break-words">
                {entityName}
                {entityUid && (
                  <span className="block text-xs opacity-70">{entityUid}</span>
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 gap-1.5"
                  onClick={() => handleQuickFeedback("correct")}
                  disabled={isLoading}
                >
                  <Check className="h-4 w-4 text-[var(--success)]" />
                  Correct
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 gap-1.5"
                  onClick={() => handleQuickFeedback("partial")}
                  disabled={isLoading}
                >
                  <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
                  Partial
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 gap-1.5"
                  onClick={() => handleQuickFeedback("incorrect")}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4 text-[var(--destructive)]" />
                  Wrong
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium">
                {feedbackType === "partial" ? "What's wrong?" : "Report issue"}
              </div>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Reason</Label>
                  <Select
                    value={reasonCode || ""}
                    onValueChange={v => setReasonCode(v as ReasonCode)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(REASON_CODE_LABELS).map(
                        ([code, label]) => (
                          <SelectItem key={code} value={code}>
                            {label}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {reasonCode &&
                  !["noise", "internal_transfer", "duplicate"].includes(
                    reasonCode
                  ) && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Corrected name</Label>
                        <Input
                          placeholder="Enter correct name..."
                          value={correctedName}
                          onChange={e => setCorrectedName(e.target.value)}
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">Swiss UID (optional)</Label>
                        <Input
                          placeholder="CHE-..."
                          value={correctedUid}
                          onChange={e => setCorrectedUid(e.target.value)}
                        />
                      </div>
                    </>
                  )}

                <div className="space-y-1.5">
                  <Label className="text-xs">Additional notes (optional)</Label>
                  <Textarea
                    placeholder="Any additional context..."
                    value={reasonText}
                    onChange={e => setReasonText(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setFeedbackType(null)}
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={handleSubmit}
                  disabled={isLoading || !reasonCode}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
